import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  createAdminUser,
  disconnectSallaConnection,
  fetchAdminSettings,
  fetchSallaConnectionStatus,
  fetchSallaPaymentMethods,
  fetchSallaSyncCategories,
  fetchAdminUsers,
  testAdminSmtp,
  updateAdminSettings,
  updateAdminUser,
} from "@/admin/api";
import type { AdminUser, SiteSettings, StorePaymentMethod } from "@/admin/types";
import MediaPicker from "@/components/feature/MediaPicker";
import { applySiteTheme } from "@/theme/applySiteTheme";

const tabKeys = ["admins", "smtp", "salla", "branding", "theme", "seo"] as const;
type TabKey = (typeof tabKeys)[number];

type AdminFormState = {
  name: string;
  email: string;
  password: string;
  role: string;
  isActive: boolean;
};

type SettingsFormState = {
  siteName: string;
  siteTagline: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  fontFamily: string;
  logoUrl: string;
  faviconUrl: string;
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string;
  seoOgImageUrl: string;
  smtpEnabled: boolean;
  smtpHost: string;
  smtpPort: string;
  smtpUser: string;
  smtpPassword: string;
  smtpFromName: string;
  smtpFromEmail: string;
  smtpSecurity: "TLS" | "SSL";
  smtpAllowSelfSigned: boolean;
  sallaSyncActiveOnly: boolean;
  sallaSyncCategoryIds: string[];
  sallaSyncZeroStockThreshold: string;
  sallaSyncEnabled: boolean;
  sallaSyncTimezone: string;
  sallaSyncIntervalMinutes: string;
  sallaSyncLastStartedAt: string;
  sallaSyncLastCompletedAt: string;
  sallaSyncNextRunAt: string;
  sallaCheckoutRedirectMode: "store_cart" | "direct_checkout" | "manual_payment_request" | "salla_order";
  sallaOrderPaymentMethod: string;
  sallaOrderShippingCompanyId: string;
  sallaOrderShippingCompanyName: string;
  sallaOrderShippingCost: string;
  sallaOrderShipToCountryId: string;
  sallaOrderShipToCityId: string;
  sallaOrderShipToDistrictId: string;
  sallaOrderShipToAddress: string;
  sallaOrderShipToAddressLine: string;
  sallaOrderShipToPostalCode: string;
};

const sallaSyncIntervalOptions = [
  10,
  20,
  30,
  ...Array.from({ length: 24 }, (_, index) => (index + 1) * 60),
];

const timezoneOptions = [
  "Asia/Riyadh",
  "Asia/Dubai",
  "Asia/Kuwait",
  "Asia/Qatar",
  "Asia/Bahrain",
  "Asia/Muscat",
  "UTC",
];

const fontOptions = [
  "IBM Plex Sans Arabic",
  "Cairo",
  "Tajawal",
  "Readex Pro",
  "Raleway",
];

const buildSettingsForm = (settings?: SiteSettings): SettingsFormState => ({
  siteName: settings?.siteName || "",
  siteTagline: settings?.siteTagline || "",
  primaryColor: settings?.primaryColor || "#E8927C",
  secondaryColor: settings?.secondaryColor || "#2A9D8F",
  accentColor: settings?.accentColor || "#E9C46A",
  fontFamily: settings?.fontFamily || "IBM Plex Sans Arabic",
  logoUrl: settings?.logoUrl || "",
  faviconUrl: settings?.faviconUrl || "",
  seoTitle: settings?.seoTitle || "",
  seoDescription: settings?.seoDescription || "",
  seoKeywords: settings?.seoKeywords || "",
  seoOgImageUrl: settings?.seoOgImageUrl || "",
  smtpEnabled: settings?.smtpEnabled || false,
  smtpHost: settings?.smtpHost || "",
  smtpPort: settings?.smtpPort ? String(settings.smtpPort) : "",
  smtpUser: settings?.smtpUser || "",
  smtpPassword: settings?.smtpPassword || "",
  smtpFromName: settings?.smtpFromName || "",
  smtpFromEmail: settings?.smtpFromEmail || "",
  smtpSecurity: settings?.smtpSecurity || "TLS",
  smtpAllowSelfSigned: settings?.smtpAllowSelfSigned || false,
  sallaSyncActiveOnly: settings?.sallaSyncActiveOnly ?? true,
  sallaSyncCategoryIds: settings?.sallaSyncCategoryIds || [],
  sallaSyncZeroStockThreshold: String(settings?.sallaSyncZeroStockThreshold || 0),
  sallaSyncEnabled: settings?.sallaSyncEnabled || false,
  sallaSyncTimezone: settings?.sallaSyncTimezone || "Asia/Riyadh",
  sallaSyncIntervalMinutes: String(settings?.sallaSyncIntervalMinutes || 60),
  sallaSyncLastStartedAt: settings?.sallaSyncLastStartedAt || "",
  sallaSyncLastCompletedAt: settings?.sallaSyncLastCompletedAt || "",
  sallaSyncNextRunAt: settings?.sallaSyncNextRunAt || "",
  sallaCheckoutRedirectMode: settings?.sallaCheckoutRedirectMode || "store_cart",
  sallaOrderPaymentMethod: settings?.sallaOrderPaymentMethod || "",
  sallaOrderShippingCompanyId: settings?.sallaOrderShippingCompanyId || "",
  sallaOrderShippingCompanyName: settings?.sallaOrderShippingCompanyName || "",
  sallaOrderShippingCost: settings?.sallaOrderShippingCost || "0",
  sallaOrderShipToCountryId: settings?.sallaOrderShipToCountryId || "",
  sallaOrderShipToCityId: settings?.sallaOrderShipToCityId || "",
  sallaOrderShipToDistrictId: settings?.sallaOrderShipToDistrictId || "",
  sallaOrderShipToAddress: settings?.sallaOrderShipToAddress || "",
  sallaOrderShipToAddressLine: settings?.sallaOrderShipToAddressLine || "",
  sallaOrderShipToPostalCode: settings?.sallaOrderShipToPostalCode || "",
});

export default function AdminSettings() {
  const { t, i18n } = useTranslation("common");
  const isRTL = i18n.language === "ar";
  const [activeTab, setActiveTab] = useState<TabKey>("admins");
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [editingAdminId, setEditingAdminId] = useState<string | null>(null);
  const [adminForm, setAdminForm] = useState<AdminFormState>({
    name: "",
    email: "",
    password: "",
    role: "Super Admin",
    isActive: true,
  });
  const [settingsForm, setSettingsForm] = useState<SettingsFormState>(buildSettingsForm());
  const [smtpTestEmail, setSmtpTestEmail] = useState("");
  const [smtpTestResult, setSmtpTestResult] = useState<"success" | "error" | "">("");
  const [sallaCategories, setSallaCategories] = useState<{ id: string; name: string }[]>([]);
  const [sallaCategoriesLoading, setSallaCategoriesLoading] = useState(false);
  const [sallaPaymentMethods, setSallaPaymentMethods] = useState<StorePaymentMethod[]>([]);
  const [sallaPaymentMethodsLoading, setSallaPaymentMethodsLoading] = useState(false);
  const [sallaConnection, setSallaConnection] = useState<{
    connected: boolean;
    installation?: {
      storeId: string;
      storeName?: string | null;
      storeDomain?: string | null;
      updatedAt: string;
    } | null;
  } | null>(null);
  const [sallaStatusLoading, setSallaStatusLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadAdmins = () => fetchAdminUsers().then(setAdmins).catch(() => setAdmins([]));

  useEffect(() => {
    let mounted = true;
    Promise.all([fetchAdminSettings(), loadAdmins()])
      .then(([settings]) => {
        if (!mounted) return;
        setSettingsForm(buildSettingsForm(settings));
      })
      .catch(() => {
        if (!mounted) return;
        setSettingsForm(buildSettingsForm());
      })
      .finally(() => {
        if (!mounted) return;
        setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (activeTab !== "salla" || sallaConnection || sallaStatusLoading) return;

    setSallaStatusLoading(true);
    fetchSallaConnectionStatus()
      .then(setSallaConnection)
      .catch(() => setSallaConnection({ connected: false, installation: null }))
      .finally(() => setSallaStatusLoading(false));
  }, [activeTab, sallaConnection, sallaStatusLoading]);

  useEffect(() => {
    if (
      activeTab !== "salla" ||
      !sallaConnection?.connected ||
      sallaCategories.length > 0 ||
      sallaCategoriesLoading
    ) return;

    setSallaCategoriesLoading(true);
    fetchSallaSyncCategories()
      .then(setSallaCategories)
      .catch(() => setSallaCategories([]))
      .finally(() => setSallaCategoriesLoading(false));
  }, [activeTab, sallaConnection?.connected, sallaCategories.length, sallaCategoriesLoading]);

  useEffect(() => {
    if (
      activeTab !== "salla" ||
      !sallaConnection?.connected ||
      sallaPaymentMethods.length > 0 ||
      sallaPaymentMethodsLoading
    ) return;

    setSallaPaymentMethodsLoading(true);
    fetchSallaPaymentMethods()
      .then((methods) => {
        setSallaPaymentMethods(methods);
        if (
          settingsForm.sallaOrderPaymentMethod &&
          !methods.some((method) => method.slug === settingsForm.sallaOrderPaymentMethod)
        ) {
          setSettingsForm((prev) => ({ ...prev, sallaOrderPaymentMethod: "" }));
        }
      })
      .catch(() => setSallaPaymentMethods([]))
      .finally(() => setSallaPaymentMethodsLoading(false));
  }, [
    activeTab,
    sallaConnection?.connected,
    sallaPaymentMethods.length,
    sallaPaymentMethodsLoading,
    settingsForm.sallaOrderPaymentMethod,
  ]);

  const resetAdminForm = () => {
    setEditingAdminId(null);
    setAdminForm({ name: "", email: "", password: "", role: "Super Admin", isActive: true });
  };

  const handleAdminEdit = (admin: AdminUser) => {
    setEditingAdminId(admin.id);
    setAdminForm({
      name: admin.name,
      email: admin.email,
      password: "",
      role: admin.role,
      isActive: admin.isActive,
    });
  };

  const handleAdminSubmit = async () => {
    setError("");
    setSuccess("");

    if (!adminForm.name.trim() || !adminForm.email.trim()) {
      setError(t("adminFillRequired"));
      return;
    }

    if (!editingAdminId && adminForm.password.trim().length < 6) {
      setError(t("adminPasswordMin"));
      return;
    }

    setIsSaving(true);
    try {
      if (editingAdminId) {
        await updateAdminUser(editingAdminId, {
          name: adminForm.name.trim(),
          email: adminForm.email.trim(),
          role: adminForm.role,
          isActive: adminForm.isActive,
          ...(adminForm.password.trim() ? { password: adminForm.password.trim() } : {}),
        });
      } else {
        await createAdminUser({
          name: adminForm.name.trim(),
          email: adminForm.email.trim(),
          role: adminForm.role,
          password: adminForm.password.trim(),
          isActive: adminForm.isActive,
        });
      }
      await loadAdmins();
      resetAdminForm();
      setSuccess(t("adminSaveSuccess"));
    } catch {
      setError(t("adminSaveFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  const adminRoleOptions = useMemo(() => [
    { value: "Super Admin", label: t("adminRoleSuper") },
    { value: "Content Editor", label: t("adminRoleEditor") },
  ], [t]);

  const saveSettings = async (payload: Partial<SettingsFormState>) => {
    setError("");
    setSuccess("");
    setIsSaving(true);
    try {
      const normalized = {
        ...payload,
        smtpPort: payload.smtpPort ? Number(payload.smtpPort) : undefined,
        sallaSyncZeroStockThreshold: payload.sallaSyncZeroStockThreshold !== undefined
          ? Number(payload.sallaSyncZeroStockThreshold || 0)
          : undefined,
        sallaSyncIntervalMinutes: payload.sallaSyncIntervalMinutes !== undefined
          ? Number(payload.sallaSyncIntervalMinutes || 60)
          : undefined,
      } as Record<string, unknown>;
      const response = await updateAdminSettings(normalized);
      setSettingsForm(buildSettingsForm(response));
      applySiteTheme(response);
      setSuccess(t("adminSaveSuccess"));
    } catch {
      setError(t("adminSaveFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  const saveBranding = () => {
    return saveSettings({
      siteName: settingsForm.siteName,
      siteTagline: settingsForm.siteTagline,
      logoUrl: settingsForm.logoUrl,
      faviconUrl: settingsForm.faviconUrl,
    });
  };

  const saveTheme = () => {
    return saveSettings({
      primaryColor: settingsForm.primaryColor,
      secondaryColor: settingsForm.secondaryColor,
      accentColor: settingsForm.accentColor,
      fontFamily: settingsForm.fontFamily,
    });
  };

  const saveSeo = () => {
    return saveSettings({
      seoTitle: settingsForm.seoTitle,
      seoDescription: settingsForm.seoDescription,
      seoKeywords: settingsForm.seoKeywords,
      seoOgImageUrl: settingsForm.seoOgImageUrl,
    });
  };

  const saveSmtp = () => {
    return saveSettings({
      smtpEnabled: settingsForm.smtpEnabled,
      smtpHost: settingsForm.smtpHost,
      smtpPort: settingsForm.smtpPort,
      smtpUser: settingsForm.smtpUser,
      smtpPassword: settingsForm.smtpPassword,
      smtpFromName: settingsForm.smtpFromName,
      smtpFromEmail: settingsForm.smtpFromEmail,
      smtpSecurity: settingsForm.smtpSecurity,
      smtpAllowSelfSigned: settingsForm.smtpAllowSelfSigned,
    });
  };

  const saveSalla = () => {
    if (settingsForm.sallaCheckoutRedirectMode === "salla_order" && !settingsForm.sallaOrderPaymentMethod) {
      setError(t("adminSallaOrderPaymentMethodRequired"));
      return;
    }
    return saveSettings({
      sallaSyncActiveOnly: settingsForm.sallaSyncActiveOnly,
      sallaSyncCategoryIds: settingsForm.sallaSyncCategoryIds,
      sallaSyncZeroStockThreshold: settingsForm.sallaSyncZeroStockThreshold,
      sallaSyncEnabled: settingsForm.sallaSyncEnabled,
      sallaSyncTimezone: settingsForm.sallaSyncTimezone,
      sallaSyncIntervalMinutes: settingsForm.sallaSyncIntervalMinutes,
      sallaCheckoutRedirectMode: settingsForm.sallaCheckoutRedirectMode,
      sallaOrderPaymentMethod: settingsForm.sallaOrderPaymentMethod,
    });
  };

  const handleDisconnectSalla = async () => {
    const confirmed = window.confirm(t("adminSallaDisconnectConfirm"));
    if (!confirmed) return;

    setError("");
    setSuccess("");
    setIsSaving(true);
    try {
      await disconnectSallaConnection();
      const settings = await fetchAdminSettings();
      setSettingsForm(buildSettingsForm(settings));
      setSallaConnection({ connected: false, installation: null });
      setSallaCategories([]);
      setSuccess(t("adminSallaDisconnectSuccess"));
    } catch {
      setError(t("adminSallaDisconnectFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  const toggleSallaCategory = (categoryId: string) => {
    setSettingsForm((prev) => {
      const selected = new Set(prev.sallaSyncCategoryIds);
      if (selected.has(categoryId)) {
        selected.delete(categoryId);
      } else {
        selected.add(categoryId);
      }
      return {
        ...prev,
        sallaSyncCategoryIds: Array.from(selected),
      };
    });
  };

  const getTabLabel = (tab: TabKey) => {
    if (tab === "salla") return t("adminSallaTab");
    return t(`adminSettingsTab${tab.charAt(0).toUpperCase()}${tab.slice(1)}`);
  };

  const formatSyncInterval = (minutes: number) => {
    if (minutes < 60) return t("adminEveryMinutes", { count: minutes });
    return t("adminEveryHours", { count: minutes / 60 });
  };

  const formatSallaDate = (value: string) => {
    if (!value) return t("adminNotSet");
    return new Intl.DateTimeFormat(isRTL ? "ar-SA" : "en-US", {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: settingsForm.sallaSyncTimezone || "Asia/Riyadh",
    }).format(new Date(value));
  };

  const applyPreset = (preset: { host: string; port: string; security: "TLS" | "SSL" }) => {
    setSettingsForm((prev) => ({
      ...prev,
      smtpEnabled: true,
      smtpHost: preset.host,
      smtpPort: preset.port,
      smtpSecurity: preset.security,
      smtpUser: prev.smtpUser || "",
      smtpFromEmail: prev.smtpFromEmail || prev.smtpUser || "",
    }));
  };

  const runSmtpTest = async () => {
    setError("");
    setSuccess("");
    setSmtpTestResult("");
    if (!smtpTestEmail.trim()) {
      setError(t("adminFillRequired"));
      return;
    }

    setIsSaving(true);
    try {
      await testAdminSmtp(smtpTestEmail.trim());
      setSmtpTestResult("success");
    } catch {
      setSmtpTestResult("error");
      setError(t("adminSmtpTestFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="text-sm text-gray-500">{t("loading")}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-800">{t("adminSettings")}</h2>
          <p className="text-sm text-gray-500 mt-1">{t("adminSettingsHint")}</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
        <div className="flex flex-wrap border-b border-gray-100 bg-gray-50/50 px-2 pt-2">
          {tabKeys.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => { setActiveTab(tab); setError(""); setSuccess(""); }}
              className={`px-5 py-3 text-sm font-semibold transition-all rounded-t-xl mx-1 relative top-[1px] ${
                activeTab === tab 
                  ? "bg-white text-teal-600 border-t border-l border-r border-gray-100 border-b-white" 
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-100/50 border border-transparent"
              }`}
            >
              {getTabLabel(tab)}
            </button>
          ))}
        </div>

        <div className="p-6 space-y-6">
          {activeTab === "admins" && (
            <div className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("adminName")}</label>
                  <input
                    value={adminForm.name}
                    onChange={(event) => setAdminForm((prev) => ({ ...prev, name: event.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("adminEmail")}</label>
                  <input
                    value={adminForm.email}
                    onChange={(event) => setAdminForm((prev) => ({ ...prev, email: event.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("adminPassword")}</label>
                  <input
                    type="password"
                    value={adminForm.password}
                    onChange={(event) => setAdminForm((prev) => ({ ...prev, password: event.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
                    placeholder={editingAdminId ? t("adminPasswordOptional") : ""}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("adminRole")}</label>
                  <select
                    value={adminForm.role}
                    onChange={(event) => setAdminForm((prev) => ({ ...prev, role: event.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all bg-white"
                  >
                    {adminRoleOptions.map((role) => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-3">
                  <div className="relative flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="admin-active"
                        type="checkbox"
                        checked={adminForm.isActive}
                        onChange={(event) => setAdminForm((prev) => ({ ...prev, isActive: event.target.checked }))}
                        className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="admin-active" className="font-medium text-gray-700">
                        {t("adminStatusActive")}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 pb-6 border-b border-gray-100">
                <button
                  type="button"
                  onClick={handleAdminSubmit}
                  disabled={isSaving}
                  className="px-5 py-2.5 rounded-xl bg-teal-500 hover:bg-teal-600 text-white text-sm font-semibold transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {isSaving && (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  )}
                  {editingAdminId ? t("adminUpdate") : t("adminCreate")}
                </button>
                {editingAdminId && (
                  <button
                    type="button"
                    onClick={resetAdminForm}
                    className="px-5 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium transition-colors"
                  >
                    {t("adminCancel")}
                  </button>
                )}
              </div>

              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
                <div className="grid grid-cols-5 gap-4 px-6 py-4 bg-gray-50/50 border-b border-gray-100 text-xs font-semibold text-gray-500">
                  <span>{t("adminName")}</span>
                  <span>{t("adminEmail")}</span>
                  <span>{t("adminRole")}</span>
                  <span>{t("adminStatus")}</span>
                  <span className="text-left">{t("adminActions")}</span>
                </div>
                <div className="divide-y divide-gray-50">
                  {admins.map((admin) => (
                    <div key={admin.id} className="grid grid-cols-5 gap-4 px-6 py-4 text-sm items-center hover:bg-gray-50/50 transition-colors">
                      <span className="text-gray-800 font-medium">{admin.name}</span>
                      <span className="text-gray-600">{admin.email}</span>
                      <span className="text-gray-600">
                        <span className="bg-gray-100 text-gray-700 px-2.5 py-1 rounded-md text-xs font-medium">
                          {admin.role}
                        </span>
                      </span>
                      <div>
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                          admin.isActive ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"
                        }`}>
                          {admin.isActive ? t("adminActive") : t("adminInactive")}
                        </span>
                      </div>
                      <div className="flex items-center justify-start" dir="ltr">
                        <button
                          type="button"
                          onClick={() => handleAdminEdit(admin)}
                          className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                          title={t("adminEdit")}
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === "smtp" && (
            <div className="space-y-6">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => applyPreset({ host: "smtp.gmail.com", port: "587", security: "TLS" })}
                  className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium transition-colors"
                >
                  {t("adminSmtpPresetGmail")}
                </button>
                <button
                  type="button"
                  onClick={() => applyPreset({ host: "smtp.office365.com", port: "587", security: "TLS" })}
                  className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium transition-colors"
                >
                  {t("adminSmtpPresetMicrosoft")}
                </button>
                <button
                  type="button"
                  onClick={() => applyPreset({ host: "smtp.mail.yahoo.com", port: "465", security: "SSL" })}
                  className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium transition-colors"
                >
                  {t("adminSmtpPresetYahoo")}
                </button>
                <button
                  type="button"
                  onClick={() => applyPreset({ host: "smtp.mail.me.com", port: "587", security: "TLS" })}
                  className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium transition-colors"
                >
                  {t("adminSmtpPresetIcloud")}
                </button>
                <button
                  type="button"
                  onClick={() => applyPreset({ host: "smtp.zoho.com", port: "587", security: "TLS" })}
                  className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium transition-colors"
                >
                  {t("adminSmtpPresetZoho")}
                </button>
                <span className="text-xs text-gray-400">{t("adminSmtpPresetHint")}</span>
              </div>
              <div className="flex items-center gap-3 bg-gray-50 p-4 rounded-xl border border-gray-100">
                <div className="relative flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="smtp-enabled"
                      type="checkbox"
                      checked={settingsForm.smtpEnabled}
                      onChange={(event) => setSettingsForm((prev) => ({ ...prev, smtpEnabled: event.target.checked }))}
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="smtp-enabled" className="font-medium text-gray-700">
                      {t("adminSmtpEnabled")}
                    </label>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("adminSmtpHost")}</label>
                  <input
                    value={settingsForm.smtpHost}
                    onChange={(event) => setSettingsForm((prev) => ({ ...prev, smtpHost: event.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("adminSmtpSecurity")}</label>
                  <select
                    value={settingsForm.smtpSecurity}
                    onChange={(event) => setSettingsForm((prev) => ({ ...prev, smtpSecurity: event.target.value as "TLS" | "SSL" }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all bg-white"
                  >
                    <option value="TLS">{t("adminSmtpSecurityTls")}</option>
                    <option value="SSL">{t("adminSmtpSecuritySsl")}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("adminSmtpPort")}</label>
                  <input
                    value={settingsForm.smtpPort}
                    onChange={(event) => setSettingsForm((prev) => ({ ...prev, smtpPort: event.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <div className="relative flex items-start mt-4">
                    <div className="flex items-center h-5">
                      <input
                        id="smtp-self-signed"
                        type="checkbox"
                        checked={settingsForm.smtpAllowSelfSigned}
                        onChange={(event) => setSettingsForm((prev) => ({ ...prev, smtpAllowSelfSigned: event.target.checked }))}
                        className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="smtp-self-signed" className="font-medium text-gray-700">
                        {t("adminSmtpAllowSelfSigned")}
                      </label>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("adminSmtpUser")}</label>
                  <input
                    value={settingsForm.smtpUser}
                    onChange={(event) => setSettingsForm((prev) => ({ ...prev, smtpUser: event.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("adminSmtpPassword")}</label>
                  <input
                    type="password"
                    value={settingsForm.smtpPassword}
                    onChange={(event) => setSettingsForm((prev) => ({ ...prev, smtpPassword: event.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("adminSmtpFromName")}</label>
                  <input
                    value={settingsForm.smtpFromName}
                    onChange={(event) => setSettingsForm((prev) => ({ ...prev, smtpFromName: event.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("adminSmtpFromEmail")}</label>
                  <input
                    value={settingsForm.smtpFromEmail}
                    onChange={(event) => setSettingsForm((prev) => ({ ...prev, smtpFromEmail: event.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-gray-50 rounded-xl border border-gray-100">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("adminSmtpTestEmail")}</label>
                  <input
                    value={smtpTestEmail}
                    onChange={(event) => setSmtpTestEmail(event.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
                    placeholder="test@example.com"
                  />
                </div>
                <div className="flex items-end gap-3">
                  <button
                    type="button"
                    onClick={runSmtpTest}
                    disabled={isSaving}
                    className="px-5 py-2.5 rounded-xl bg-gray-800 hover:bg-gray-900 text-white text-sm font-semibold transition-colors disabled:opacity-50"
                  >
                    {t("adminSmtpTestButton")}
                  </button>
                  {smtpTestResult === "success" && (
                    <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-lg border border-emerald-100 flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      {t("adminSmtpTestSuccess")}
                    </span>
                  )}
                  {smtpTestResult === "error" && (
                    <span className="text-xs font-medium text-red-600 bg-red-50 px-3 py-1.5 rounded-lg border border-red-100 flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      {t("adminSmtpTestFailed")}
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={saveSmtp}
                disabled={isSaving}
                className="px-5 py-2.5 rounded-xl bg-teal-500 hover:bg-teal-600 text-white text-sm font-semibold transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isSaving && (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                )}
                {t("adminSaveSettings")}
              </button>
            </div>
          )}

          {activeTab === "salla" && (
            <div className="space-y-6">
              <div className="rounded-xl border border-gray-100 bg-white p-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    {t("adminSallaConnectionStatus")}
                  </p>
                  {sallaStatusLoading ? (
                    <p className="text-xs text-gray-400 mt-1">{t("loading")}</p>
                  ) : sallaConnection?.connected ? (
                    <p className="text-xs text-gray-500 mt-1">
                      {(sallaConnection.installation?.storeName || sallaConnection.installation?.storeDomain || sallaConnection.installation?.storeId || "")}
                      {sallaConnection.installation?.storeId ? ` - ${sallaConnection.installation.storeId}` : ""}
                    </p>
                  ) : (
                    <p className="text-xs text-amber-600 mt-1">
                      {t("adminSallaNoConnection")}
                    </p>
                  )}
                </div>

                <button
                  type="button"
                  onClick={handleDisconnectSalla}
                  disabled={isSaving || sallaStatusLoading || !sallaConnection?.connected}
                  className="px-4 py-2 rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm font-semibold hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {t("adminSallaDisconnect")}
                </button>
              </div>

              <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600">
                    {t("adminSallaCheckoutRedirectMode")}
                  </label>
                  <p className="text-xs text-gray-400 mt-1">
                    {t("adminSallaCheckoutRedirectModeHint")}
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                  <label className="flex items-start gap-3 p-3 bg-white border border-gray-100 rounded-xl text-sm text-gray-700 hover:border-teal-200 cursor-pointer">
                    <input
                      type="radio"
                      checked={settingsForm.sallaCheckoutRedirectMode === "store_cart"}
                      onChange={() => setSettingsForm((prev) => ({ ...prev, sallaCheckoutRedirectMode: "store_cart" }))}
                      className="w-4 h-4 mt-0.5 text-teal-600 border-gray-300 focus:ring-teal-500"
                    />
                    <span>
                      <span className="block font-semibold text-gray-800">{t("adminSallaRedirectToCart")}</span>
                      <span className="block text-xs text-gray-400 mt-1">{t("adminSallaRedirectToCartHint")}</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 p-3 bg-white border border-gray-100 rounded-xl text-sm text-gray-700 hover:border-teal-200 cursor-pointer">
                    <input
                      type="radio"
                      checked={settingsForm.sallaCheckoutRedirectMode === "direct_checkout"}
                      onChange={() => setSettingsForm((prev) => ({ ...prev, sallaCheckoutRedirectMode: "direct_checkout" }))}
                      className="w-4 h-4 mt-0.5 text-teal-600 border-gray-300 focus:ring-teal-500"
                    />
                    <span>
                      <span className="block font-semibold text-gray-800">{t("adminSallaRedirectToCheckout")}</span>
                      <span className="block text-xs text-gray-400 mt-1">{t("adminSallaRedirectToCheckoutHint")}</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 p-3 bg-white border border-gray-100 rounded-xl text-sm text-gray-700 hover:border-teal-200 cursor-pointer">
                    <input
                      type="radio"
                      checked={settingsForm.sallaCheckoutRedirectMode === "manual_payment_request"}
                      onChange={() => setSettingsForm((prev) => ({ ...prev, sallaCheckoutRedirectMode: "manual_payment_request" }))}
                      className="w-4 h-4 mt-0.5 text-teal-600 border-gray-300 focus:ring-teal-500"
                    />
                    <span>
                      <span className="block font-semibold text-gray-800">{t("adminSallaRedirectToPaymentRequest")}</span>
                      <span className="block text-xs text-gray-400 mt-1">{t("adminSallaRedirectToPaymentRequestHint")}</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 p-3 bg-white border border-gray-100 rounded-xl text-sm text-gray-700 hover:border-teal-200 cursor-pointer">
                    <input
                      type="radio"
                      checked={settingsForm.sallaCheckoutRedirectMode === "salla_order"}
                      onChange={() => setSettingsForm((prev) => ({ ...prev, sallaCheckoutRedirectMode: "salla_order" }))}
                      className="w-4 h-4 mt-0.5 text-teal-600 border-gray-300 focus:ring-teal-500"
                    />
                    <span>
                      <span className="block font-semibold text-gray-800">{t("adminSallaRedirectToSallaOrder")}</span>
                      <span className="block text-xs text-gray-400 mt-1">{t("adminSallaRedirectToSallaOrderHint")}</span>
                    </span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                    {t("adminSallaOrderPaymentMethod")}
                  </label>
                  <select
                    value={settingsForm.sallaOrderPaymentMethod}
                    onChange={(event) => setSettingsForm((prev) => ({
                      ...prev,
                      sallaOrderPaymentMethod: event.target.value,
                    }))}
                    disabled={!sallaConnection?.connected || sallaPaymentMethodsLoading}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm bg-white disabled:bg-gray-50 disabled:text-gray-400"
                  >
                    <option value="">
                      {sallaPaymentMethodsLoading ? t("loading") : t("adminSallaOrderPaymentMethodPlaceholder")}
                    </option>
                    {sallaPaymentMethods.map((method) => (
                      <option key={method.slug} value={method.slug}>
                        {method.name || method.slug}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-400 mt-1.5">
                    {t("adminSallaOrderPaymentMethodHint")}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <input
                      type="checkbox"
                      checked={settingsForm.sallaSyncEnabled}
                      onChange={(event) => setSettingsForm((prev) => ({
                        ...prev,
                        sallaSyncEnabled: event.target.checked,
                      }))}
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    {t("adminSallaAutoSync")}
                  </label>
                  <p className="text-xs text-gray-400 mt-2">
                    {t("adminSallaAutoSyncHint")}
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("adminSallaTimezone")}</label>
                  <select
                    value={settingsForm.sallaSyncTimezone}
                    onChange={(event) => setSettingsForm((prev) => ({
                      ...prev,
                      sallaSyncTimezone: event.target.value,
                    }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all bg-white"
                  >
                    {timezoneOptions.map((timezone) => (
                      <option key={timezone} value={timezone}>{timezone}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("adminSallaSyncInterval")}</label>
                  <select
                    value={settingsForm.sallaSyncIntervalMinutes}
                    onChange={(event) => setSettingsForm((prev) => ({
                      ...prev,
                      sallaSyncIntervalMinutes: event.target.value,
                    }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all bg-white"
                  >
                    {sallaSyncIntervalOptions.map((option) => (
                      <option key={option} value={String(option)}>
                        {formatSyncInterval(option)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-white border border-gray-100 rounded-xl">
                  <p className="text-xs font-semibold text-gray-500 mb-1">{t("adminSallaLastStarted")}</p>
                  <p className="text-sm text-gray-800">{formatSallaDate(settingsForm.sallaSyncLastStartedAt)}</p>
                </div>
                <div className="p-4 bg-white border border-gray-100 rounded-xl">
                  <p className="text-xs font-semibold text-gray-500 mb-1">{t("adminSallaLastCompleted")}</p>
                  <p className="text-sm text-gray-800">{formatSallaDate(settingsForm.sallaSyncLastCompletedAt)}</p>
                </div>
                <div className="p-4 bg-white border border-gray-100 rounded-xl">
                  <p className="text-xs font-semibold text-gray-500 mb-1">{t("adminSallaNextRun")}</p>
                  <p className="text-sm text-gray-800">{formatSallaDate(settingsForm.sallaSyncNextRunAt)}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 space-y-3">
                  <label className="block text-xs font-semibold text-gray-600">{t("adminSallaProductPullType")}</label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      checked={settingsForm.sallaSyncActiveOnly}
                      onChange={() => setSettingsForm((prev) => ({ ...prev, sallaSyncActiveOnly: true }))}
                      className="w-4 h-4 text-teal-600 border-gray-300 focus:ring-teal-500"
                    />
                    {t("adminSallaActiveOnly")}
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      checked={!settingsForm.sallaSyncActiveOnly}
                      onChange={() => setSettingsForm((prev) => ({ ...prev, sallaSyncActiveOnly: false }))}
                      className="w-4 h-4 text-teal-600 border-gray-300 focus:ring-teal-500"
                    />
                    {t("adminSallaAllProducts")}
                  </label>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                    {t("adminSallaLocalStockThreshold")}
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={settingsForm.sallaSyncZeroStockThreshold}
                    onChange={(event) => setSettingsForm((prev) => ({
                      ...prev,
                      sallaSyncZeroStockThreshold: event.target.value,
                    }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
                  />
                  <p className="text-xs text-gray-400 mt-2">
                    {t("adminSallaLocalStockThresholdHint")}
                  </p>
                </div>
              </div>

              {sallaStatusLoading ? (
                <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 text-sm text-gray-400 text-center">
                  {t("loading")}
                </div>
              ) : !sallaConnection?.connected ? (
                <div className="rounded-xl border border-amber-100 bg-amber-50 p-4 text-sm text-amber-700">
                  {t("adminSallaCategoriesNeedConnection")}
                </div>
              ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600">{t("adminSallaCategories")}</label>
                    <p className="text-xs text-gray-400 mt-1">
                      {t("adminSallaCategoriesHint")}
                    </p>
                  </div>
                  {settingsForm.sallaSyncCategoryIds.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setSettingsForm((prev) => ({ ...prev, sallaSyncCategoryIds: [] }))}
                      className="text-xs font-semibold text-teal-600 hover:text-teal-700"
                    >
                      {t("adminClearSelection")}
                    </button>
                  )}
                </div>

                <div className="min-h-[120px] rounded-xl border border-gray-100 bg-gray-50 p-3">
                  {sallaCategoriesLoading ? (
                    <div className="text-sm text-gray-400 py-8 text-center">{t("loading")}</div>
                  ) : sallaCategories.length === 0 ? (
                    <div className="text-sm text-gray-400 py-8 text-center">
                      {t("adminSallaNoCategories")}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {sallaCategories.map((category) => (
                        <label
                          key={category.id}
                          className="flex items-center gap-2 p-3 bg-white border border-gray-100 rounded-xl text-sm text-gray-700 hover:border-teal-200 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={settingsForm.sallaSyncCategoryIds.includes(category.id)}
                            onChange={() => toggleSallaCategory(category.id)}
                            className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                          />
                          <span className="truncate">{category.name}</span>
                          <span className="text-[10px] text-gray-400 font-mono ms-auto">{category.id}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              )}

              <button
                type="button"
                onClick={saveSalla}
                disabled={isSaving}
                className="px-5 py-2.5 rounded-xl bg-teal-500 hover:bg-teal-600 text-white text-sm font-semibold transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isSaving && (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                )}
                {t("adminSaveSettings")}
              </button>
            </div>
          )}

          {activeTab === "branding" && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("adminSiteName")}</label>
                  <input
                    value={settingsForm.siteName}
                    onChange={(event) => setSettingsForm((prev) => ({ ...prev, siteName: event.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("adminSiteTagline")}</label>
                  <input
                    value={settingsForm.siteTagline}
                    onChange={(event) => setSettingsForm((prev) => ({ ...prev, siteTagline: event.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
                  />
                </div>
                <div className="md:col-span-2">
                  <MediaPicker
                    label={t("adminLogoUrl")}
                    value={settingsForm.logoUrl}
                    onChange={(value) => setSettingsForm((prev) => ({ ...prev, logoUrl: value }))}
                  />
                </div>
                <div className="md:col-span-2">
                  <MediaPicker
                    label={t("adminFaviconUrl")}
                    value={settingsForm.faviconUrl}
                    onChange={(value) => setSettingsForm((prev) => ({ ...prev, faviconUrl: value }))}
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={saveBranding}
                disabled={isSaving}
                className="px-5 py-2.5 rounded-xl bg-teal-500 hover:bg-teal-600 text-white text-sm font-semibold transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isSaving && (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                )}
                {t("adminSaveSettings")}
              </button>
            </div>
          )}

          {activeTab === "theme" && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("adminPrimaryColor")}</label>
                  <div className="flex items-center gap-3">
                    <div className="relative w-12 h-12 rounded-xl overflow-hidden border border-gray-200 shadow-sm flex-shrink-0">
                      <input
                        type="color"
                        value={settingsForm.primaryColor}
                        onChange={(event) => setSettingsForm((prev) => ({ ...prev, primaryColor: event.target.value }))}
                        className="absolute -inset-4 w-20 h-20 cursor-pointer"
                      />
                    </div>
                    <input
                      value={settingsForm.primaryColor}
                      onChange={(event) => setSettingsForm((prev) => ({ ...prev, primaryColor: event.target.value }))}
                      className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all font-mono uppercase"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("adminSecondaryColor")}</label>
                  <div className="flex items-center gap-3">
                    <div className="relative w-12 h-12 rounded-xl overflow-hidden border border-gray-200 shadow-sm flex-shrink-0">
                      <input
                        type="color"
                        value={settingsForm.secondaryColor}
                        onChange={(event) => setSettingsForm((prev) => ({ ...prev, secondaryColor: event.target.value }))}
                        className="absolute -inset-4 w-20 h-20 cursor-pointer"
                      />
                    </div>
                    <input
                      value={settingsForm.secondaryColor}
                      onChange={(event) => setSettingsForm((prev) => ({ ...prev, secondaryColor: event.target.value }))}
                      className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all font-mono uppercase"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("adminAccentColor")}</label>
                  <div className="flex items-center gap-3">
                    <div className="relative w-12 h-12 rounded-xl overflow-hidden border border-gray-200 shadow-sm flex-shrink-0">
                      <input
                        type="color"
                        value={settingsForm.accentColor}
                        onChange={(event) => setSettingsForm((prev) => ({ ...prev, accentColor: event.target.value }))}
                        className="absolute -inset-4 w-20 h-20 cursor-pointer"
                      />
                    </div>
                    <input
                      value={settingsForm.accentColor}
                      onChange={(event) => setSettingsForm((prev) => ({ ...prev, accentColor: event.target.value }))}
                      className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all font-mono uppercase"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("adminFontFamily")}</label>
                  <select
                    value={settingsForm.fontFamily}
                    onChange={(event) => setSettingsForm((prev) => ({ ...prev, fontFamily: event.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all bg-white"
                  >
                    {fontOptions.map((font) => (
                      <option key={font} value={font}>{font}</option>
                    ))}
                  </select>
                </div>
              </div>
              <button
                type="button"
                onClick={saveTheme}
                disabled={isSaving}
                className="px-5 py-2.5 rounded-xl bg-teal-500 hover:bg-teal-600 text-white text-sm font-semibold transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isSaving && (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                )}
                {t("adminSaveSettings")}
              </button>
            </div>
          )}

          {activeTab === "seo" && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("adminSeoTitle")}</label>
                  <input
                    value={settingsForm.seoTitle}
                    onChange={(event) => setSettingsForm((prev) => ({ ...prev, seoTitle: event.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("adminSeoKeywords")}</label>
                  <input
                    value={settingsForm.seoKeywords}
                    onChange={(event) => setSettingsForm((prev) => ({ ...prev, seoKeywords: event.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("adminSeoDescription")}</label>
                  <textarea
                    value={settingsForm.seoDescription}
                    onChange={(event) => setSettingsForm((prev) => ({ ...prev, seoDescription: event.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all min-h-[120px] resize-y"
                  />
                </div>
                <div className="md:col-span-2">
                  <MediaPicker
                    label={t("adminSeoOgImage")}
                    value={settingsForm.seoOgImageUrl}
                    onChange={(value) => setSettingsForm((prev) => ({ ...prev, seoOgImageUrl: value }))}
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={saveSeo}
                disabled={isSaving}
                className="px-5 py-2.5 rounded-xl bg-teal-500 hover:bg-teal-600 text-white text-sm font-semibold transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isSaving && (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                )}
                {t("adminSaveSettings")}
              </button>
            </div>
          )}

          {error && <p className="text-xs text-red-500">{error}</p>}
          {success && <p className="text-xs text-green-600">{success}</p>}
        </div>
      </div>
    </div>
  );
}
